import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res =
      mode === "login"
        ? await login(email, password, remember)
        : await register(email, password, name || email.split("@")[0], remember);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="min-h-[85vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1593824261342-fd6ee146f73d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Activity className="w-10 h-10 text-phosphor mb-4" />
          <h2 className="text-4xl font-extrabold tracking-tight">
            Clock in to the <span className="text-phosphor">training floor.</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground max-w-md">
            Your progress, streaks, and spaced-repetition queue follow you across devices.
          </p>
          <div className="mt-10 text-[10px] tracking-[0.3em] text-muted-foreground">
            GSOFTWARE LLC // CLINICAL SIMULATOR
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md panel p-8" data-testid="auth-form">
          <div className="flex gap-6 mb-8 border-b border-border">
            {[
              ["login", "Sign In"],
              ["register", "Create Account"],
            ].map(([m, lbl]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`auth-tab-${m}`}
                className={`pb-3 text-xs tracking-[0.3em] uppercase transition-colors ${
                  mode === m
                    ? "text-phosphor border-b-2 border-phosphor -mb-px"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <Field label="Name">
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="auth-input"
                autoComplete="name"
                placeholder="Jane Doe"
              />
            </Field>
          )}
          <Field label="Email">
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              autoComplete="email"
              placeholder="you@hospital.com"
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder="At least 6 characters"
            />
          </Field>

          <label className="flex items-center gap-3 mb-5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="auth-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="auth-remember-checkbox"
              className="w-4 h-4 accent-[hsl(142_71%_45%)] bg-transparent border border-phosphor/40"
            />
            <span>Keep me signed in on this device <span className="opacity-60">(30 days)</span></span>
          </label>

          {err && (
            <div data-testid="auth-error" className="text-xs text-destructive mb-4">
              {err}
            </div>
          )}

          <button
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full bg-primary text-primary-foreground py-3 text-sm font-semibold tracking-[0.25em] uppercase hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {busy ? "..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>

          <div className="mt-6 text-[11px] text-muted-foreground text-center">
            <Link to="/" className="hover:text-foreground">← Back to home</Link>
          </div>
        </form>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          background: transparent;
          border: 1px solid hsl(142 71% 45% / 0.25);
          padding: 0.75rem 0.9rem;
          font-family: 'JetBrains Mono', monospace;
          color: hsl(210 40% 98%);
          font-size: 0.85rem;
          outline: none;
          transition: border-color 120ms, box-shadow 120ms;
        }
        .auth-input:focus {
          border-color: hsl(142 71% 55%);
          box-shadow: 0 0 0 1px hsl(142 71% 45% / 0.4), 0 0 10px hsl(142 71% 45% / 0.25);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-5">
      <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">{label}</div>
      {children}
    </label>
  );
}
